import React, { useState, useEffect, useMemo } from 'react';
import s from './SentEmailsTab.module.sass';
import { notifyError, notifyWarning, notifySuccess } from '../../../utils/notify';

// Группы статусов для сводки-фильтра
const STATUS_GROUPS = {
    transit: ['pending', 'queued', 'scheduled', 'sent', 'delivery_delayed'],
    delivered: ['delivered', 'opened', 'clicked'],
    failed: ['failed', 'bounced', 'complained', 'suppressed', 'canceled']
};

// Прогресс письма по шагам таймлайна: Отправлено → Доставлено → Открыто
const STEP_PROGRESS = {
    pending: 0, queued: 0, scheduled: 0,
    sent: 1, delivery_delayed: 1,
    delivered: 2,
    opened: 3, clicked: 3
};

const SentEmailsTab = () => {
    const [emails, setEmails] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedStatus, setSelectedStatus] = useState('');
    const [selectedGroup, setSelectedGroup] = useState(''); // '' | 'transit' | 'delivered' | 'failed'
    const [selectedConfigs, setSelectedConfigs] = useState([]); // Множественный выбор конфигов
    const [showConfigDropdown, setShowConfigDropdown] = useState(false);
    const [showStatusDropdown, setShowStatusDropdown] = useState(false);
    const [showItemsPerPageDropdown, setShowItemsPerPageDropdown] = useState(false);
    const [itemsPerPage, setItemsPerPage] = useState(25);
    const [currentPage, setCurrentPage] = useState(1);
    const [expandedCards, setExpandedCards] = useState(new Set());
    const [availableConfigs, setAvailableConfigs] = useState([]);
    const [checkingStatuses, setCheckingStatuses] = useState(new Set());
    // Модалка просмотра фактически отправленного письма (тело хранится 7 дней)
    const [bodyModal, setBodyModal] = useState({ open: false, loading: false, html: '', subject: '', sentAt: '', error: '' });
    const getAuthHeaders = () => {
        const token = localStorage.getItem('authToken');
        return token ? { Authorization: `Bearer ${token}` } : {};
    };

    const openEmailBody = async (emailId) => {
        setBodyModal({ open: true, loading: true, html: '', subject: '', sentAt: '', error: '' });
        try {
            const response = await fetch(`/api/config/email/sent/${encodeURIComponent(emailId)}/body`, {
                headers: getAuthHeaders()
            });
            const data = await response.json();
            if (!response.ok || !data.success) {
                setBodyModal(m => ({ ...m, loading: false, error: data.error || 'Не удалось загрузить письмо' }));
                return;
            }
            setBodyModal({ open: true, loading: false, html: data.html || '', subject: data.subject || '', sentAt: data.sentAt || '', error: '' });
        } catch (e) {
            setBodyModal(m => ({ ...m, loading: false, error: 'Ошибка сети при загрузке письма' }));
        }
    };
    const closeBodyModal = () => setBodyModal({ open: false, loading: false, html: '', subject: '', sentAt: '', error: '' });

    useEffect(() => {
        loadEmails();
        loadConfigs();
    }, []);

    const loadConfigs = async () => {
        try {
            const { getLocalConfigsList } = await import('../../../utils/configUtils');
            const configs = await getLocalConfigsList();
            setAvailableConfigs(configs);
        } catch (err) {
            console.error('Ошибка загрузки конфигов:', err);
        }
    };

    const loadEmails = async (forceRefresh = false) => {
        try {
            setLoading(true);
            setError(null);
            const response = await fetch(`/api/config/email/sent?refreshLimit=${itemsPerPage}${forceRefresh ? '&forceRefresh=1' : ''}`, {
                headers: getAuthHeaders()
            });
            if (response.ok) {
                const data = await response.json();
                setEmails(Array.isArray(data.emails) ? data.emails : []);
            } else {
                setEmails([]);
            }
        } catch (err) {
            console.error('Ошибка загрузки отправленных писем:', err);
            setEmails([]);
        } finally {
            setLoading(false);
        }
    };

    const handleConfigToggle = (configFilename) => {
        setSelectedConfigs(prev => {
            if (prev.includes(configFilename)) {
                return prev.filter(c => c !== configFilename);
            } else {
                return [...prev, configFilename];
            }
        });
    };

    const checkEmailStatus = async (messageId, emailId) => {
        if (!messageId) {
            notifyWarning('ID письма не найден');
            return;
        }

        try {
            setCheckingStatuses(prev => new Set(prev).add(emailId));
            const response = await fetch(`/api/config/email/status/${encodeURIComponent(messageId)}`, {
                headers: getAuthHeaders()
            });
            const data = await response.json();
            
            if (data.success) {
                await loadEmails(); // Перезагружаем список для обновления статуса
            } else {
                notifyError('Ошибка проверки статуса: ' + (data.error || ''));
            }
        } catch (err) {
            console.error('Ошибка проверки статуса:', err);
            notifyError('Ошибка проверки статуса: ' + err.message);
        } finally {
            setCheckingStatuses(prev => {
                const newSet = new Set(prev);
                newSet.delete(emailId);
                return newSet;
            });
        }
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!event.target.closest(`.${s.dropdownWrapper}`)) {
                setShowConfigDropdown(false);
                setShowStatusDropdown(false);
                setShowItemsPerPageDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside, true);
        return () => document.removeEventListener('mousedown', handleClickOutside, true);
    }, []);

    const toggleCard = (emailId) => {
        setExpandedCards(prev => {
            const newSet = new Set(prev);
            if (newSet.has(emailId)) {
                newSet.delete(emailId);
            } else {
                newSet.add(emailId);
            }
            return newSet;
        });
    };

    const formatDate = (dateString) => {
        if (!dateString) return '—';
        try {
            const date = new Date(dateString);
            return new Intl.DateTimeFormat('ru-RU', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit'
            }).format(date);
        } catch {
            return dateString;
        }
    };

    const getStatusLabel = (status) => {
        const statusMap = {
            'pending': { label: 'Ожидает', color: '#8A92A3' },
            'queued': { label: 'В очереди', color: '#8A92A3' },
            'scheduled': { label: 'Запланировано', color: '#8A92A3' },
            'sent': { label: 'Отправлено', color: '#4DA3FF' },
            'delivered': { label: 'Доставлено', color: '#3ECF8E' },
            'opened': { label: 'Открыто', color: '#34D399' },
            'clicked': { label: 'Клик', color: '#2DD4BF' },
            'delivery_delayed': { label: 'Задержка доставки', color: '#E8B44C' },
            'failed': { label: 'Ошибка', color: '#F06A6A' },
            'bounced': { label: 'Отклонено', color: '#F08A4C' },
            'complained': { label: 'Жалоба', color: '#E05252' },
            'suppressed': { label: 'Подавлено', color: '#B07A3F' },
            'canceled': { label: 'Отменено', color: '#78808F' }
        };
        return statusMap[status] || { label: status || 'Неизвестно', color: '#8A92A3' };
    };

    const getDeliveryEventLabel = (event) => {
        const normalizedEvent = String(event || '').toLowerCase().replace(/^email\./, '');
        return getStatusLabel(normalizedEvent).label;
    };

    const statusOptions = [
        { value: '', label: 'Все статусы' },
        { value: 'pending', label: 'Ожидает' },
        { value: 'queued', label: 'В очереди' },
        { value: 'scheduled', label: 'Запланировано' },
        { value: 'sent', label: 'Отправлено' },
        { value: 'delivered', label: 'Доставлено' },
        { value: 'opened', label: 'Открыто' },
        { value: 'clicked', label: 'Клик' },
        { value: 'delivery_delayed', label: 'Задержка доставки' },
        { value: 'failed', label: 'Ошибка' },
        { value: 'bounced', label: 'Отклонено' },
        { value: 'complained', label: 'Жалоба' },
        { value: 'suppressed', label: 'Подавлено' },
        { value: 'canceled', label: 'Отменено' }
    ];

    const filteredEmails = emails.filter(email => {
        // Фильтр по поисковому запросу
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            const matchesSearch = (
                (email.to_email || '').toLowerCase().includes(query) ||
                (email.subject || '').toLowerCase().includes(query) ||
                (email.templateName || '').toLowerCase().includes(query) ||
                (email.sentBy || '').toLowerCase().includes(query) ||
                (email.configFile || '').toLowerCase().includes(query)
            );
            if (!matchesSearch) return false;
        }

        // Фильтр по статусу
        if (selectedStatus && email.status !== selectedStatus) {
            return false;
        }

        // Фильтр по группе статусов (сводные карточки)
        if (selectedGroup && !(STATUS_GROUPS[selectedGroup] || []).includes(email.status)) {
            return false;
        }

        // Фильтр по конфигу (множественный выбор)
        if (selectedConfigs.length > 0) {
            const emailConfig = email.configFile || '';
            const matchesConfig = selectedConfigs.some(selectedConfigFilename => {
                return emailConfig === selectedConfigFilename || 
                       emailConfig === selectedConfigFilename + '.json' || 
                       emailConfig + '.json' === selectedConfigFilename;
            });
            if (!matchesConfig) return false;
        }

        return true;
    });

    // Пагинация
    const totalPages = Math.ceil(filteredEmails.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedEmails = filteredEmails.slice(startIndex, endIndex);

    // Сброс на первую страницу при изменении фильтров или поиска
    useEffect(() => {
        setCurrentPage(1);
    }, [searchQuery, itemsPerPage, selectedStatus, selectedConfigs, selectedGroup]);

    // Сводка по всем письмам для карточек-фильтров
    const summary = useMemo(() => {
        const counts = { total: emails.length, transit: 0, delivered: 0, failed: 0 };
        emails.forEach((email) => {
            if (STATUS_GROUPS.transit.includes(email.status)) counts.transit += 1;
            else if (STATUS_GROUPS.delivered.includes(email.status)) counts.delivered += 1;
            else if (STATUS_GROUPS.failed.includes(email.status)) counts.failed += 1;
        });
        return counts;
    }, [emails]);

    // Группировка страницы по дням, как в почтовом клиенте
    const dayLabel = (dateString) => {
        if (!dateString) return 'Без даты';
        const date = new Date(dateString);
        if (Number.isNaN(date.getTime())) return 'Без даты';
        const today = new Date();
        const yesterday = new Date();
        yesterday.setDate(today.getDate() - 1);
        if (date.toDateString() === today.toDateString()) return 'Сегодня';
        if (date.toDateString() === yesterday.toDateString()) return 'Вчера';
        return date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });
    };

    const groupedByDay = useMemo(() => {
        const groups = [];
        let current = null;
        paginatedEmails.forEach((email) => {
            const label = dayLabel(email.sentAt);
            if (!current || current.label !== label) {
                current = { label, items: [] };
                groups.push(current);
            }
            current.items.push(email);
        });
        return groups;
    }, [paginatedEmails]);

    const formatTime = (dateString) => {
        if (!dateString) return '—';
        try {
            return new Date(dateString).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
        } catch {
            return '—';
        }
    };

    const copyToClipboard = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            notifySuccess('Message ID скопирован');
        } catch {
            notifyError('Не удалось скопировать');
        }
    };

    if (loading) {
        return (
            <div className={s.loading}>
                <div className={s.spinner}></div>
                <p>Загрузка отправленных писем...</p>
            </div>
        );
    }

    return (
        <div className={s.sentEmailsTab}>
            <div className={s.controls}>
                <div className={s.searchContainer}>
                    <svg className={s.searchIcon} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="11" cy="11" r="8"></circle>
                        <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                    </svg>
                    <input
                        type="text"
                        className={s.searchInput}
                        placeholder="Поиск по email, теме, шаблону, саппу, конфигу..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    {searchQuery && (
                        <button type="button" className={s.searchClear} onClick={() => setSearchQuery('')}>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                        </button>
                    )}
                </div>

                <div className={s.dropdownWrapper}>
                    <button
                        type="button"
                        className={`${s.dropdownButton} ${selectedStatus ? s.dropdownActive : ''}`}
                        onClick={() => {
                            setShowStatusDropdown(!showStatusDropdown);
                            setShowConfigDropdown(false);
                            setShowItemsPerPageDropdown(false);
                        }}
                    >
                        <svg className={s.ddIcon} width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10"></circle>
                            <polyline points="12 6 12 12 16 14"></polyline>
                        </svg>
                        <span>{statusOptions.find((option) => option.value === selectedStatus)?.label || 'Все статусы'}</span>
                        <svg className={s.ddChevron} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points={showStatusDropdown ? "18 15 12 9 6 15" : "6 9 12 15 18 9"}></polyline>
                        </svg>
                    </button>
                    {showStatusDropdown && (
                        <div className={s.dropdownMenu}>
                            {statusOptions.map((option) => (
                                <button
                                    key={option.value || 'all'}
                                    type="button"
                                    className={`${s.dropdownOptionButton} ${selectedStatus === option.value ? s.active : ''}`}
                                    onClick={() => {
                                        setSelectedStatus(option.value);
                                        setShowStatusDropdown(false);
                                    }}
                                >
                                    <span>{option.label}</span>
                                    {selectedStatus === option.value && <span className={s.dropdownOptionCheck}>✓</span>}
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                <div className={s.dropdownWrapper}>
                    <button
                        type="button"
                        className={`${s.dropdownButton} ${selectedConfigs.length > 0 ? s.dropdownActive : ''}`}
                        onClick={() => {
                            setShowConfigDropdown(!showConfigDropdown);
                            setShowStatusDropdown(false);
                            setShowItemsPerPageDropdown(false);
                        }}
                    >
                        <svg className={s.ddIcon} width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                            <polyline points="7 10 12 15 17 10"></polyline>
                            <line x1="12" y1="15" x2="12" y2="3"></line>
                        </svg>
                        <span>
                            {selectedConfigs.length === 0
                                ? 'Все конфиги'
                                : selectedConfigs.length === 1
                                    ? (availableConfigs.find(c => (c.filename || c.name || c) === selectedConfigs[0])?.name || selectedConfigs[0].replace('.json', ''))
                                    : `Конфигов: ${selectedConfigs.length}`
                            }
                        </span>
                        <svg className={s.ddChevron} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points={showConfigDropdown ? "18 15 12 9 6 15" : "6 9 12 15 18 9"}></polyline>
                        </svg>
                    </button>
                    {showConfigDropdown && (
                        <div className={s.dropdownMenu}>
                            <div className={s.dropdownHeader}>
                                <span>Выберите конфиги</span>
                                {selectedConfigs.length > 0 && (
                                    <button className={s.clearAllButton} onClick={() => setSelectedConfigs([])}>
                                        Очистить
                                    </button>
                                )}
                            </div>
                            <div className={s.checkboxList}>
                                {availableConfigs.map((config) => {
                                    const configFilename = config.filename || config.name || config;
                                    const configName = config.name || configFilename.replace('.json', '') || configFilename;
                                    const isSelected = selectedConfigs.includes(configFilename);
                                    return (
                                        <label key={configFilename} className={s.checkboxItem}>
                                            <input
                                                type="checkbox"
                                                checked={isSelected}
                                                onChange={() => handleConfigToggle(configFilename)}
                                            />
                                            <span>{configName}</span>
                                        </label>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>

                <div className={s.dropdownWrapper}>
                    <button
                        type="button"
                        className={`${s.dropdownButton} ${s.perPageButton}`}
                        title="Писем на странице"
                        onClick={() => {
                            setShowItemsPerPageDropdown(!showItemsPerPageDropdown);
                            setShowConfigDropdown(false);
                            setShowStatusDropdown(false);
                        }}
                    >
                        <svg className={s.ddIcon} width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="8" y1="6" x2="21" y2="6"></line>
                            <line x1="8" y1="12" x2="21" y2="12"></line>
                            <line x1="8" y1="18" x2="21" y2="18"></line>
                            <line x1="3" y1="6" x2="3.01" y2="6"></line>
                            <line x1="3" y1="12" x2="3.01" y2="12"></line>
                            <line x1="3" y1="18" x2="3.01" y2="18"></line>
                        </svg>
                        <span>{itemsPerPage}</span>
                        <svg className={s.ddChevron} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points={showItemsPerPageDropdown ? "18 15 12 9 6 15" : "6 9 12 15 18 9"}></polyline>
                        </svg>
                    </button>
                    {showItemsPerPageDropdown && (
                        <div className={`${s.dropdownMenu} ${s.itemsPerPageMenu}`}>
                            {[10, 25, 50, 100].map((option) => (
                                <button
                                    key={option}
                                    type="button"
                                    className={`${s.dropdownOptionButton} ${itemsPerPage === option ? s.active : ''}`}
                                    onClick={() => {
                                        setItemsPerPage(option);
                                        setShowItemsPerPageDropdown(false);
                                    }}
                                >
                                    <span>{option}</span>
                                    {itemsPerPage === option && <span className={s.dropdownOptionCheck}>✓</span>}
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                <button onClick={() => loadEmails(true)} className={s.refreshButton} title="Обновить список">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="23 4 23 10 17 10"></polyline>
                        <polyline points="1 20 1 14 7 14"></polyline>
                        <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
                    </svg>
                    <span>Обновить</span>
                </button>
            </div>

            <div className={s.summaryRow}>
                <button
                    type="button"
                    className={`${s.summaryCard} ${selectedGroup === '' ? s.summaryCardActive : ''}`}
                    onClick={() => setSelectedGroup('')}
                >
                    <span className={s.summaryIcon} data-tone="neutral">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="2" y="4" width="20" height="16" rx="2"></rect>
                            <path d="m22 7-10 5L2 7"></path>
                        </svg>
                    </span>
                    <span className={s.summaryText}>
                        <span className={s.summaryValue}>{summary.total}</span>
                        <span className={s.summaryLabel}>Всего</span>
                    </span>
                </button>
                <button
                    type="button"
                    className={`${s.summaryCard} ${selectedGroup === 'transit' ? s.summaryCardActive : ''}`}
                    onClick={() => setSelectedGroup(selectedGroup === 'transit' ? '' : 'transit')}
                    title="Ожидают, в очереди, отправлены — ещё не доставлены"
                >
                    <span className={s.summaryIcon} data-tone="info">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M22 2 11 13"></path>
                            <path d="M22 2 15 22 11 13 2 9 22 2z"></path>
                        </svg>
                    </span>
                    <span className={s.summaryText}>
                        <span className={s.summaryValue}>{summary.transit}</span>
                        <span className={s.summaryLabel}>В пути</span>
                    </span>
                </button>
                <button
                    type="button"
                    className={`${s.summaryCard} ${selectedGroup === 'delivered' ? s.summaryCardActive : ''}`}
                    onClick={() => setSelectedGroup(selectedGroup === 'delivered' ? '' : 'delivered')}
                    title="Доставлены, открыты, были клики"
                >
                    <span className={s.summaryIcon} data-tone="good">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10"></circle>
                            <polyline points="9 12 11 14 15 10"></polyline>
                        </svg>
                    </span>
                    <span className={s.summaryText}>
                        <span className={s.summaryValue}>{summary.delivered}</span>
                        <span className={s.summaryLabel}>Доставлено</span>
                    </span>
                </button>
                <button
                    type="button"
                    className={`${s.summaryCard} ${selectedGroup === 'failed' ? s.summaryCardActive : ''}`}
                    onClick={() => setSelectedGroup(selectedGroup === 'failed' ? '' : 'failed')}
                    title="Ошибки, отклонённые, жалобы"
                >
                    <span className={s.summaryIcon} data-tone="bad">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10"></circle>
                            <line x1="15" y1="9" x2="9" y2="15"></line>
                            <line x1="9" y1="9" x2="15" y2="15"></line>
                        </svg>
                    </span>
                    <span className={s.summaryText}>
                        <span className={s.summaryValue}>{summary.failed}</span>
                        <span className={s.summaryLabel}>Ошибки</span>
                    </span>
                </button>
                {(searchQuery || selectedStatus || selectedConfigs.length > 0 || selectedGroup) && (
                    <span className={s.foundNote}>Найдено: <strong>{filteredEmails.length}</strong></span>
                )}
            </div>

            {filteredEmails.length === 0 ? (
                <div className={s.empty}>
                    <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className={s.emptyIcon}>
                        <path d="M22 2 11 13"></path>
                        <path d="M22 2 15 22 11 13 2 9 22 2z"></path>
                    </svg>
                    <p>{searchQuery || selectedStatus || selectedConfigs.length > 0 || selectedGroup ? 'Письма не найдены' : 'Отправленные письма отсутствуют'}</p>
                    {(searchQuery || selectedStatus || selectedConfigs.length > 0 || selectedGroup) && (
                        <button onClick={() => {
                            setSearchQuery('');
                            setSelectedStatus('');
                            setSelectedConfigs([]);
                            setSelectedGroup('');
                        }} className={s.clearButton}>
                            Очистить фильтры
                        </button>
                    )}
                </div>
            ) : (
                <>
                    <div className={s.emailsList}>
                        {groupedByDay.map((group) => (
                            <div key={group.label} className={s.dayGroup}>
                                <div className={s.dayHeader}>
                                    <span className={s.dayLabel}>{group.label}</span>
                                    <span className={s.dayLine} />
                                    <span className={s.dayCount}>{group.items.length}</span>
                                </div>
                                {group.items.map((email) => {
                                    const isExpanded = expandedCards.has(email.id);
                                    const statusInfo = getStatusLabel(email.status);
                                    const checkableStatuses = ['pending', 'queued', 'scheduled', 'sent', 'delivery_delayed'];
                                    const resendCheckableStatuses = [...checkableStatuses, 'delivered', 'opened', 'clicked'];
                                    const canCheckStatus = email.messageId && (
                                        email.provider === 'resend'
                                            ? resendCheckableStatuses.includes(email.status)
                                            : checkableStatuses.includes(email.status)
                                    );
                                    const isFailed = STATUS_GROUPS.failed.includes(email.status);
                                    const progress = STEP_PROGRESS[email.status] ?? 0;

                                    return (
                                        <div key={email.id} className={`${s.emailCard} ${isExpanded ? s.emailCardOpen : ''}`}>
                                            <div
                                                className={s.cardHeader}
                                                onClick={() => toggleCard(email.id)}
                                            >
                                                <span className={s.emailIcon} style={{ color: statusInfo.color, background: statusInfo.color + '1A', borderColor: statusInfo.color + '40' }}>
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <rect x="2" y="4" width="20" height="16" rx="2"></rect>
                                                        <path d="m22 7-10 5L2 7"></path>
                                                    </svg>
                                                </span>
                                                <div className={s.emailInfo}>
                                                    <div className={s.emailTopRow}>
                                                        <span className={s.emailTo}>{email.to_email || '—'}</span>
                                                        {email.generatedCode && (
                                                            <span className={s.codeTag} title="Сгенерированный код из письма">
                                                                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                    <path d="M16 18l6-6-6-6"></path>
                                                                    <path d="M8 6l-6 6 6 6"></path>
                                                                </svg>
                                                                <span>{email.generatedCode}</span>
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className={s.emailSubject}>{email.subject || '—'}</div>
                                                    <div className={s.emailMeta}>
                                                        {(email.templateName || email.templateId) && (
                                                            <span className={s.metaChip} title="Шаблон">
                                                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                    <rect x="4" y="4" width="16" height="16" rx="2"></rect>
                                                                    <line x1="8" y1="8" x2="16" y2="8"></line>
                                                                    <line x1="8" y1="12" x2="16" y2="12"></line>
                                                                </svg>
                                                                <span>{email.templateName || email.templateId}</span>
                                                            </span>
                                                        )}
                                                        {email.configFile && (
                                                            <span className={s.metaChip} title="Конфиг">
                                                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                                                    <polyline points="7 10 12 15 17 10"></polyline>
                                                                    <line x1="12" y1="15" x2="12" y2="3"></line>
                                                                </svg>
                                                                <span>{email.configFile.replace('.json', '')}</span>
                                                            </span>
                                                        )}
                                                        {email.sentBy && (
                                                            <span className={s.metaChip} title="Отправил">
                                                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                                                                    <circle cx="12" cy="7" r="4"></circle>
                                                                </svg>
                                                                <span>{email.sentBy}</span>
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className={s.rightCol}>
                                                    <span
                                                        className={s.statusBadge}
                                                        style={{ backgroundColor: statusInfo.color + '1A', color: statusInfo.color, borderColor: statusInfo.color + '40' }}
                                                    >
                                                        <span className={s.statusDot} style={{ background: statusInfo.color }} />
                                                        {statusInfo.label}
                                                    </span>
                                                    <span className={s.emailTime} title={formatDate(email.sentAt)}>{formatTime(email.sentAt)}</span>
                                                </div>
                                                <div className={`${s.expandIcon} ${isExpanded ? s.expanded : ''}`}>
                                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <polyline points="6 9 12 15 18 9"></polyline>
                                                    </svg>
                                                </div>
                                            </div>

                                            {isExpanded && (
                                                <div className={s.cardBody}>
                                                    <div className={s.timeline}>
                                                        {['Отправлено', 'Доставлено', 'Открыто'].map((label, i) => {
                                                            const done = !isFailed && progress >= i + 1;
                                                            const isCurrent = !isFailed && progress === i;
                                                            return (
                                                                <React.Fragment key={label}>
                                                                    {i > 0 && <span className={`${s.timelineLine} ${done ? s.timelineLineDone : ''}`} />}
                                                                    <span className={`${s.timelineStep} ${done ? s.timelineStepDone : ''} ${isCurrent ? s.timelineStepCurrent : ''}`}>
                                                                        <span className={s.timelineDot}>
                                                                            {done && (
                                                                                <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5">
                                                                                    <polyline points="20 6 9 17 4 12"></polyline>
                                                                                </svg>
                                                                            )}
                                                                        </span>
                                                                        <span className={s.timelineLabel}>{label}</span>
                                                                    </span>
                                                                </React.Fragment>
                                                            );
                                                        })}
                                                        {isFailed && (
                                                            <span className={s.timelineFail}>
                                                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                    <circle cx="12" cy="12" r="10"></circle>
                                                                    <line x1="15" y1="9" x2="9" y2="15"></line>
                                                                    <line x1="9" y1="9" x2="15" y2="15"></line>
                                                                </svg>
                                                                <span>{statusInfo.label}</span>
                                                            </span>
                                                        )}
                                                    </div>

                                                    <div className={s.infoGrid}>
                                                        <div className={s.infoRow}>
                                                            <span className={s.label}>Шаблон</span>
                                                            <span className={s.value}>{email.templateName || email.templateId || '—'}</span>
                                                        </div>
                                                        <div className={s.infoRow}>
                                                            <span className={s.label}>Конфиг</span>
                                                            <span className={s.value}>{email.configFile ? email.configFile.replace('.json', '') : '—'}</span>
                                                        </div>
                                                        <div className={s.infoRow}>
                                                            <span className={s.label}>Отправил</span>
                                                            <span className={s.value}>{email.sentBy || '—'}</span>
                                                        </div>
                                                        <div className={s.infoRow}>
                                                            <span className={s.label}>Провайдер</span>
                                                            <span className={s.value}>{email.provider || '—'}</span>
                                                        </div>
                                                        <div className={`${s.infoRow} ${s.infoRowWide}`}>
                                                            <span className={s.label}>Message ID</span>
                                                            <span className={s.valueRow}>
                                                                <span className={`${s.value} ${s.valueMono}`}>{email.messageId || '—'}</span>
                                                                {email.messageId && (
                                                                    <button
                                                                        type="button"
                                                                        className={s.copyBtn}
                                                                        title="Скопировать Message ID"
                                                                        onClick={(e) => { e.stopPropagation(); copyToClipboard(email.messageId); }}
                                                                    >
                                                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                            <rect x="9" y="9" width="13" height="13" rx="2"></rect>
                                                                            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                                                                        </svg>
                                                                    </button>
                                                                )}
                                                            </span>
                                                        </div>
                                                        {email.error && (
                                                            <div className={`${s.infoRow} ${s.infoRowWide}`}>
                                                                <span className={s.label}>Ошибка</span>
                                                                <span className={`${s.value} ${s.errorText}`}>{email.error}</span>
                                                            </div>
                                                        )}
                                                        {email.generatedCode && (
                                                            <div className={s.infoRow}>
                                                                <span className={s.label}>Код</span>
                                                                <span className={`${s.value} ${s.valueCode}`}>{email.generatedCode}</span>
                                                            </div>
                                                        )}
                                                        {email.deliveryStatus && (
                                                            <div className={`${s.infoRow} ${s.infoRowWide}`}>
                                                                <span className={s.label}>Последнее событие</span>
                                                                <span className={s.value}>
                                                                    {getDeliveryEventLabel(email.deliveryStatus.event || email.deliveryStatus.last_event)}
                                                                    {(email.deliveryStatus.timestamp || email.deliveryStatus.created_at) && ` (${formatDate(email.deliveryStatus.timestamp || email.deliveryStatus.created_at)})`}
                                                                    {email.deliveryStatus.provider && `, ${email.deliveryStatus.provider}`}
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>
                                                    {(canCheckStatus || email.hasBody) && (
                                                        <div className={s.cardActions}>
                                                            {canCheckStatus && (
                                                                <button
                                                                    className={s.checkStatusButton}
                                                                    onClick={() => checkEmailStatus(email.messageId, email.id)}
                                                                    disabled={checkingStatuses.has(email.id)}
                                                                >
                                                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                        <polyline points="23 4 23 10 17 10"></polyline>
                                                                        <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path>
                                                                    </svg>
                                                                    <span>{checkingStatuses.has(email.id) ? 'Проверка...' : 'Проверить статус'}</span>
                                                                </button>
                                                            )}
                                                            {email.hasBody && (
                                                                <button
                                                                    className={s.viewBodyButton}
                                                                    onClick={() => openEmailBody(email.id)}
                                                                >
                                                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                        <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z"></path>
                                                                        <circle cx="12" cy="12" r="3"></circle>
                                                                    </svg>
                                                                    <span>Посмотреть письмо</span>
                                                                </button>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        ))}
                    </div>

                    {totalPages > 1 && (
                        <div className={s.pagination}>
                            <button
                                className={s.pageButton}
                                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                disabled={currentPage === 1}
                            >
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <polyline points="15 18 9 12 15 6"></polyline>
                                </svg>
                                <span>Назад</span>
                            </button>

                            <div className={s.pageNumbers}>
                                {Array.from({ length: totalPages }, (_, i) => i + 1)
                                    .filter(page => {
                                        if (page === 1 || page === totalPages) return true;
                                        if (Math.abs(page - currentPage) <= 1) return true;
                                        return false;
                                    })
                                    .map((page, index, array) => {
                                        const prevPage = array[index - 1];
                                        const showEllipsis = prevPage && page - prevPage > 1;

                                        return (
                                            <React.Fragment key={page}>
                                                {showEllipsis && <span className={s.ellipsis}>…</span>}
                                                <button
                                                    className={`${s.pageNumber} ${currentPage === page ? s.active : ''}`}
                                                    onClick={() => setCurrentPage(page)}
                                                >
                                                    {page}
                                                </button>
                                            </React.Fragment>
                                        );
                                    })}
                            </div>

                            <button
                                className={s.pageButton}
                                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                disabled={currentPage === totalPages}
                            >
                                <span>Вперед</span>
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <polyline points="9 18 15 12 9 6"></polyline>
                                </svg>
                            </button>
                        </div>
                    )}
                </>
            )}

            {bodyModal.open && (
                <div className={s.bodyModalOverlay} onClick={closeBodyModal}>
                    <div className={s.bodyModal} onClick={e => e.stopPropagation()}>
                        <div className={s.bodyModalHeader}>
                            <div className={s.bodyModalTitle}>
                                <span className={s.bodyModalSubject}>{bodyModal.subject || 'Отправленное письмо'}</span>
                                {bodyModal.sentAt && <span className={s.bodyModalDate}>{formatDate(bodyModal.sentAt)}</span>}
                            </div>
                            <button className={s.bodyModalClose} onClick={closeBodyModal} aria-label="Закрыть">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <line x1="18" y1="6" x2="6" y2="18"></line>
                                    <line x1="6" y1="6" x2="18" y2="18"></line>
                                </svg>
                            </button>
                        </div>
                        <div className={s.bodyModalContent}>
                            {bodyModal.loading && <div className={s.bodyModalState}>Загрузка…</div>}
                            {!bodyModal.loading && bodyModal.error && (
                                <div className={s.bodyModalState}>{bodyModal.error}</div>
                            )}
                            {!bodyModal.loading && !bodyModal.error && (
                                <iframe
                                    title="Тело отправленного письма"
                                    className={s.bodyModalFrame}
                                    srcDoc={bodyModal.html}
                                    sandbox=""
                                />
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SentEmailsTab;

